import { useParams } from "react-router-dom";
import { Formik } from "formik";
import React, { useEffect, useRef } from "react";
import {
    useFetchAnamnesisByPatientQuery,
    useFetchAnamnesisHistoryByPatientIdQuery,
    useSaveAnamnesisMutation,
} from "scenes/patient/anamnesis/AnamnesisApi";
import { useTranslation } from "react-i18next";
import AnamnesisFormWrapper from "./AnamnesisFormWrapper";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import { useFetchPatientByIdQuery, useUpdatePatientDetailsMutation } from "scenes/patient/PatientApi";
import Skeleton from "@material-ui/lab/Skeleton";
import { getDiff } from "../AnamnesisMapper";
import { highestRole } from "../../../../domain/User.model";

const AnamnesisForm = ({ closeDialog }) => {
    const { patientId } = useParams();
    const { data: patientData } = useFetchPatientByIdQuery(patientId);
    const [updatePatientDetails] = useUpdatePatientDetailsMutation();
    const setUserDetails = (values) => {
        updatePatientDetails({
            id: patientData.id,
            ...values,
        });
    };

    const anonymized = patientData?.anonymized;

    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const anamnesisForFormikRef = useRef(null);
    const previousAnamnesesRef = useRef(null);
    const user = useSelector((state) => state.mainReducer.user);
    const [initialValues] = React.useState({
        gender: "MALE",
        hasChipMutation: null,
        chipMutationData: [],
    });

    const {
        data: anamnesisData,
        isSuccess: anamnesisDataLoadedSuccessfully,
        isLoading: anamnesisDataIsLoading,
    } = useFetchAnamnesisByPatientQuery({ patientId }, { skip: !patientId });

    const {
        data: anamnesisHistory,
        isSuccess: anamnesisHistoryLoadedSuccessfully,
        isLoading: anamnesisHistoryIsLoading,
    } = useFetchAnamnesisHistoryByPatientIdQuery({ patientId }, { skip: !patientId });

    const [saveAnamnesis, { isSuccess: isSavingItemSuccessful, isError: isSavingItemError }] =
        useSaveAnamnesisMutation();

    const save = (form) => {
        const diff = getDiff(form.values, anamnesisHistory, { patientId, role: highestRole(user), userId: user?.id });
        console.debug("form.values: ", form.values);
        console.debug("DIFF form.values: ", diff);

        saveAnamnesis(diff);
    };

    useEffect(() => {
        if (anamnesisDataLoadedSuccessfully && anamnesisForFormikRef && previousAnamnesesRef) {
            const anamneses = { ...anamnesisData, patientId, gender: anamnesisData.gender ?? initialValues.gender };
            anamnesisForFormikRef.current.setValues(anamneses);
            previousAnamnesesRef.current = JSON.stringify(anamneses);
        }
    }, [
        anamnesisDataLoadedSuccessfully,
        anamnesisData,
        anamnesisForFormikRef,
        initialValues,
        patientId,
        previousAnamnesesRef,
    ]);

    useEffect(() => {
        if (isSavingItemSuccessful) {
            enqueueSnackbar(t("global.data.saved.success"), { variant: "success" });
        }
        if (isSavingItemError) {
            enqueueSnackbar(t("global.data.saved.error"), { variant: "error" });
        }
    }, [isSavingItemSuccessful, isSavingItemError]);

    const loadingSkeleton = (
        <div style={{ padding: "10px" }}>
            <Skeleton variant="text" height={46} />
            <Skeleton variant="text" height={46} />
            <Skeleton variant="rect" height={274} />
            <Skeleton variant="text" height={46} />
            <Skeleton variant="rect" height={444} />
            <Skeleton variant="text" height={46} />
            <Skeleton variant="rect" height={97} />
            <Skeleton variant="text" height={46} />
            <Skeleton variant="rect" height={500} />
        </div>
    );

    return anamnesisDataIsLoading ||
        anamnesisHistoryIsLoading ||
        !anamnesisDataLoadedSuccessfully ||
        !anamnesisHistoryLoadedSuccessfully ? (
        loadingSkeleton
    ) : (
        <Formik
            innerRef={(form) => (anamnesisForFormikRef.current = form)}
            initialValues={initialValues}
            onSubmit={() => {
                //NOTE LM: Formik was a mistake. Forms as a concept is a mistake to begin with...
                // However, it was done by an incredibly hardworking man, and I won't ever have the time to
                // rewrite 10 000 lines of utter horseshit.
                // At least DO NOT try to use formiks submit function. It's "unpredictable" ""when"" it will run.
                // There is a whole ruleset controlling when it supposed to, that you have to remember,
                // and it depends on validity states you will forget, and probably uranium decay too. So just don't.
            }}
        >
            {({ isSubmitting, ...formProps }) => (
                <AnamnesisFormWrapper
                    {...{
                        save,
                        form: formProps,
                        patientData,
                        setUserDetails,
                        user,
                        readonly: anonymized,
                        closeDialog,
                    }}
                />
            )}
        </Formik>
    );
};

export default AnamnesisForm;
